import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { FormEvent, useEffect, useState } from 'react';
import styles from './StripeElementsForPaymentMethodUpdate.module.scss';
import {
  useCreditsAccountInfo,
  useManageCreditAccountPageRightPanel,
  useSession,
  useVisible,
} from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import {
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { useUpdateCreditAccountMutation } from 'src/store/services';
import { env } from 'src/env';
import { AppRoutes, UserSettingsNestedRoutes } from 'src/types';
import { toast } from 'react-toastify';

const errorMessage =
  'We’re having trouble processing your update. Please check your information or try a different payment method.';
const successMessage = 'Payment card was successfully updated.';

type StripeElementsForPaymentMethodUpdateProps = {
  onReady: () => void;
  onUpdatePaymentProcessing: (value: boolean) => void;
  onUpdateAccountFailed: () => Promise<void>;
};

export const StripeElementsForPaymentMethodUpdate = ({
  onReady,
  onUpdatePaymentProcessing,
  onUpdateAccountFailed,
}: StripeElementsForPaymentMethodUpdateProps) => {
  const {
    appUser: { user_id, email },
  } = useSession();
  const stripe = useStripe();
  const elements = useElements();
  // const [isReadyExpressCheckout, setReadyExpressCheckout] =
  //   useState<boolean>(false);
  const [isReadyPaymentElement, setReadyPaymentElement] =
    useState<boolean>(false);
  const { changeActivePanelView } = useManageCreditAccountPageRightPanel();
  const { creditAccountInfo } = useCreditsAccountInfo();

  const [updateAccount] = useUpdateCreditAccountMutation();

  const {
    isVisible: isSubmitDisabled,
    onVisibilitySet: setSubmitDisabled,
    onVisibilityRemove: setSubmitEnabled,
  } = useVisible(true);

  useEffect(() => {
    if (
      isReadyPaymentElement
      // && isReadyExpressCheckout
    ) {
      onReady();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadyPaymentElement]);

  const handleUpdateAccount = async (payment_method: string) => {
    try {
      const result = await updateAccount({
        user_id,
        credit_account: creditAccountInfo?.account_id || '',
        paymentInfo: {
          payment_config: {
            stripe: {
              use_existing_payment_method: false,
              payment_method_id: payment_method,
            },
          },
        },
      }).unwrap();
      changeActivePanelView(null);
      toast.success(successMessage);
      onUpdatePaymentProcessing(false);
      return result;
    } catch (error) {
      onUpdatePaymentProcessing(false);
      await onUpdateAccountFailed();
      toast.error(errorMessage);
      return;
    }
  };

  const handleConfirmPayment = async () => {
    if (!elements || !stripe) {
      return;
    }

    const returnUrl = `${env.REACT_APP_NINJA_UI_URL}${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`;

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl,
        payment_method_data: {
          billing_details: {
            email,
          },
        },
      },
      redirect: 'if_required',
    });

    if (error) {
      toast.error(errorMessage);
      onUpdatePaymentProcessing(false);
      return;
    }

    if (setupIntent && setupIntent.status === 'succeeded') {
      await handleUpdateAccount((setupIntent?.payment_method || '') as string);
    } else {
      toast.error(errorMessage);
      onUpdatePaymentProcessing(false);
    }
  };

  const handleSubmit = async (
    event:
      | StripeExpressCheckoutElementConfirmEvent
      | FormEvent<HTMLFormElement>,
  ) => {
    if ('preventDefault' in event) {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      event.preventDefault();
    }

    if (!elements || !stripe) {
      return;
    }

    onUpdatePaymentProcessing(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      onUpdatePaymentProcessing(false);
      return;
    }

    await handleConfirmPayment();
  };

  // const handleExpressCheckoutClick = (
  //   event: StripeExpressCheckoutElementClickEvent,
  // ) => {
  //   event.resolve();
  // };

  const handlePaymentElementChange = ({
    complete,
  }: StripePaymentElementChangeEvent) => {
    if (complete) {
      setSubmitEnabled();
    } else {
      setSubmitDisabled();
    }
  };

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit}
      data-e2e="stripe-elements-form"
    >
      {/*<ExpressCheckoutElement*/}
      {/*  options={{*/}
      {/*    paymentMethods: {*/}
      {/*      googlePay: 'always',*/}
      {/*      link: 'never', // never so it's shown in payment element below*/}
      {/*      paypal: 'never',*/}
      {/*    },*/}
      {/*    paymentMethodOrder: ['apple_pay', 'google_pay', 'amazon_pay'],*/}
      {/*    buttonTheme: {*/}
      {/*      googlePay: 'white',*/}
      {/*      applePay: isDarkTheme ? 'white' : 'black',*/}
      {/*    },*/}
      {/*    layout: {*/}
      {/*      maxColumns: 1,*/}
      {/*      maxRows: 5,*/}
      {/*    },*/}
      {/*    buttonHeight: 53,*/}
      {/*  }}*/}
      {/*  onReady={() => setReadyExpressCheckout(true)}*/}
      {/*  onConfirm={handleSubmit}*/}
      {/*  onClick={handleExpressCheckoutClick}*/}
      {/*/>*/}
      <PaymentElement
        options={{
          layout: {
            type: 'accordion',
            defaultCollapsed: true,
            radios: false,
            spacedAccordionItems: true,
          },
          fields: {
            billingDetails: {
              email: 'never',
            },
          },
          paymentMethodOrder: ['google_pay', 'apple_pay', 'card', 'cashapp'],
        }}
        onReady={() => setReadyPaymentElement(true)}
        onChange={handlePaymentElementChange}
      />
      <hr className={styles.divider} />
      <Button
        disabled={isSubmitDisabled}
        type="submit"
        data-e2e="subscribe-submit-button"
      >
        Update payment details
      </Button>
      <Button color="transparent" onClick={() => changeActivePanelView(null)}>
        Cancel
      </Button>
    </form>
  );
};
